<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength } from '@vuelidate/validators';
import { getRegexp, normalizeRegexPattern } from 'shared/helpers/Validators';
import { ATTRIBUTE_TYPES } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

export default {
  components: {
    NextButton,
    TagInput,
  },
  props: {
    selectedAttribute: {
      type: Object,
      default: () => {},
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeType: 0,
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      show: true,
      attributeKey: '',
      values: [],
      tagInputTouched: false,
      pinned: false,
      required: false,
      conditionAttributeKey: '',
      conditionValue: '',
    };
  },
  validations: {
    displayName: { required },
    attributeType: { required },
    description: { required, minLength: minLength(1) },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  computed: {
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    setAttributeListValue() {
      return this.selectedAttribute.attribute_values || [];
    },
    updatedAttributeListValues() {
      return this.values;
    },
    isButtonDisabled() {
      return !!(
        this.v$.description.$invalid ||
        this.isTagInputEmpty ||
        (this.conditionAttributeKey && !this.conditionValue)
      );
    },
    isTagInputEmpty() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },

    pageTitle() {
      return `${this.$t('ATTRIBUTES_MGMT.EDIT.TITLE')} - ${
        this.selectedAttribute.attribute_display_name
      }`;
    },
    selectedAttributeType() {
      return this.types.find(
        item =>
          item.key.toLowerCase() ===
          this.selectedAttribute.attribute_display_type
      )?.id;
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
    isAttributeModelConversation() {
      return (
        this.selectedAttribute.attribute_model === 'conversation_attribute'
      );
    },
    conditionableAttributes() {
      return this.$store.getters['attributes/getAttributesByModel'](
        'conversation_attribute'
      ).filter(
        attribute =>
          attribute.attribute_key !== this.selectedAttribute.attribute_key
      );
    },
    selectedTriggerAttribute() {
      return this.conditionableAttributes.find(
        attribute => attribute.attribute_key === this.conditionAttributeKey
      );
    },
    isTriggerAttributeList() {
      return this.selectedTriggerAttribute?.attribute_display_type === 'list';
    },
    isTriggerAttributeCheckbox() {
      return (
        this.selectedTriggerAttribute?.attribute_display_type === 'checkbox'
      );
    },
  },
  mounted() {
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    setFormValues() {
      const regexPattern = this.selectedAttribute.regex_pattern
        ? getRegexp(this.selectedAttribute.regex_pattern).toString()
        : null;
      this.displayName = this.selectedAttribute.attribute_display_name;
      this.description = this.selectedAttribute.attribute_description;
      this.attributeType = this.selectedAttributeType;
      this.attributeKey = this.selectedAttribute.attribute_key;
      this.regexPattern = regexPattern;
      this.regexCue = this.selectedAttribute.regex_cue;
      this.regexEnabled = regexPattern != null;
      this.values = this.setAttributeListValue;
      this.pinned = this.selectedAttribute.pinned || false;
      this.required = this.selectedAttribute.required || false;
      const [firstCondition] = this.selectedAttribute.conditions || [];
      this.conditionAttributeKey = firstCondition?.attribute_key || '';
      this.conditionValue = firstCondition?.value || '';
    },
    async editAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/update', {
          id: this.selectedAttribute.id,
          attribute_description: this.description,
          attribute_display_name: this.displayName,
          attribute_values: this.updatedAttributeListValues,
          regex_pattern: normalizeRegexPattern(this.regexPattern),
          regex_cue: this.regexCue,
          pinned: this.isAttributeModelConversation ? this.pinned : false,
          required: this.isAttributeModelConversation ? this.required : false,
          conditions:
            this.isAttributeModelConversation && this.conditionAttributeKey
              ? [
                  {
                    attribute_key: this.conditionAttributeKey,
                    value: this.conditionValue,
                  },
                ]
              : [],
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col w-full" @submit.prevent="editAttributes">
      <div class="w-full">
        <woot-input
          v-model="displayName"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: v$.displayName.$error }"
          :error="
            v$.displayName.$error
              ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.displayName.$touch"
        />
        <woot-input
          v-model="attributeKey"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
          type="text"
          :class="{ error: v$.attributeKey.$error }"
          :error="v$.attributeKey.$error ? keyErrorMessage : ''"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
          readonly
          @blur="v$.attributeKey.$touch"
        />
        <label :class="{ error: v$.description.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          <textarea
            v-model="description"
            rows="5"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
          <span v-if="v$.description.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
          </span>
        </label>
        <label :class="{ error: v$.attributeType.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          <select v-model="attributeType" disabled>
            <option v-for="type in types" :key="type.id" :value="type.id">
              {{ type.option }}
            </option>
          </select>
          <span v-if="v$.attributeType.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
          </span>
        </label>
        <div v-if="isAttributeTypeList" class="mb-4">
          <label class="mb-1 block">
            {{ $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.LABEL') }}
          </label>
          <div
            class="rounded-xl border px-3 py-2"
            :class="isTagInputInvalid ? 'border-n-ruby-9' : 'border-n-weak'"
          >
            <TagInput
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              allow-create
              @blur="tagInputTouched = true"
            />
          </div>
          <label
            v-show="isTagInputInvalid"
            class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
          </label>
        </div>
        <div v-if="isAttributeTypeText">
          <input
            v-model="regexEnabled"
            type="checkbox"
            @input="toggleRegexEnabled"
          />
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
        </div>
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexPattern"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
          type="text"
          :placeholder="
            $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
          "
        />
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexCue"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
        />
        <div v-if="isAttributeModelConversation">
          <label>
            <input v-model="pinned" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.PINNED.LABEL') }}
          </label>
          <p class="text-n-slate-11 text-sm mt-1 mb-4">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.PINNED.HINT') }}
          </p>
          <label>
            <input v-model="required" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.REQUIRED.LABEL') }}
          </label>
          <p class="text-n-slate-11 text-sm mt-1 mb-4">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.REQUIRED.HINT') }}
          </p>
          <label>
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.LABEL') }}
            <select
              v-model="conditionAttributeKey"
              @change="conditionValue = ''"
            >
              <option value="">
                {{
                  $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.NO_CONDITION_OPTION')
                }}
              </option>
              <option
                v-for="attribute in conditionableAttributes"
                :key="attribute.attribute_key"
                :value="attribute.attribute_key"
              >
                {{ attribute.attribute_display_name }}
              </option>
            </select>
          </label>
          <label v-if="conditionAttributeKey">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.VALUE_LABEL') }}
            <select v-if="isTriggerAttributeList" v-model="conditionValue">
              <option value="" disabled>
                {{
                  $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.VALUE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="option in selectedTriggerAttribute.attribute_values"
                :key="option"
                :value="option"
              >
                {{ option }}
              </option>
            </select>
            <select
              v-else-if="isTriggerAttributeCheckbox"
              v-model="conditionValue"
            >
              <option value="" disabled>
                {{
                  $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.VALUE_PLACEHOLDER')
                }}
              </option>
              <option value="true">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.TRUE_LABEL') }}
              </option>
              <option value="false">
                {{ $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.FALSE_LABEL') }}
              </option>
            </select>
            <woot-input
              v-else
              v-model="conditionValue"
              type="text"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.CONDITIONS.VALUE_PLACEHOLDER')
              "
            />
          </label>
        </div>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')"
          :is-loading="isUpdating"
          :disabled="isButtonDisabled"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}
</style>
